import React, { useState, useEffect } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  Paper,
  InputAdornment,
  Typography,
  Modal,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddCourse = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [saving, setSaving] = useState(false);

    const [organizations, setOrganizations] = useState([]);


 const [formData, setFormData] = useState({
  course_name: '',
  course_duration: '',
  course_description: '',
  basic_course_fee: '',
  gst_percentage: '0',
  organization: organizations.length > 0 ? organizations[0]?.id || '' : '',
});
  
  const [formErrors, setFormErrors] = useState({});
  
  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://wims.revisetoexcel.com';

  // Get authentication token
  const getAuthToken = () => {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('access_token') ||
      sessionStorage.getItem('accessToken')
    );
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    
    if (!token) {
      console.warn('No authentication token found');
      return {
        'Content-Type': 'application/json',
      };
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const getStoredUser = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem('user') ||
      localStorage.getItem('user') ||
      '{}'
    );
  } catch {
    return {};
  }
};

 const handleInputChange = (e) => {
  const { name, value } = e.target;

  // Ignore branch changes (handled globally)
  if (name === "branch") {
    return;
  }

  setFormData(prev => ({
    ...prev,
    [name]: value
  }));

  // Clear error for this field when user starts typing
  if (formErrors[name]) {
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  }
};

  // Validate form
 const validateForm = () => {
  const errors = {};

  if (!formData.course_name.trim()) {
    errors.course_name = 'Course name is required';
  }

  if (!formData.course_duration.trim()) {
    errors.course_duration = 'Course duration is required';
  }

  if (!formData.course_description.trim()) {
    errors.course_description = 'Course description is required';
  }

  if (!formData.basic_course_fee || parseFloat(formData.basic_course_fee) <= 0) {
    errors.basic_course_fee = 'Valid course fee is required';
  }

  if (!formData.organization) {
    errors.organization = "Organization is required";
  }

  // Remove branch validation (handled globally)
  // if (!formData.branch) {
  //   errors.branch = "Branch is required";
  // }

  setFormErrors(errors);
  return Object.keys(errors).length === 0;
};

 const resetForm = () => {
  const user = getStoredUser();
  const loggedInOrgId = user?.organization_id || user?.organization || '';

  const matchedOrg = organizations.find(
    (org) => String(org.id) === String(loggedInOrgId)
  );

  setFormData({
    course_name: '',
    course_duration: '',
    course_description: '',
    basic_course_fee: '',
    gst_percentage: '0',
    organization: matchedOrg ? matchedOrg.id : (organizations[0]?.id || ''),
  });
};
const handleSave = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    setSnackbarMessage(
      "Please fill all required fields correctly."
    );
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
    return;
  }

  try {
    setSaving(true);

    const token = getAuthToken();

    if (!token) {
      setSnackbarMessage(
        "Authentication required. Please log in."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setSaving(false);
      return;
    }

    // ==================================================
    // CHECK DUPLICATE COURSE NAME
    // ==================================================

    const courseName =
      formData.course_name.trim();

    const coursesResponse = await fetch(
      `${API_BASE_URL}/api/course/courses/?branch=${selectedBranchId}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    if (coursesResponse.status === 401) {
      setSnackbarMessage(
        "Session expired. Please log in again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      localStorage.removeItem("token");
      sessionStorage.removeItem("token");

      setSaving(false);
      return;
    }

    if (!coursesResponse.ok) {
      throw new Error(
        "Unable to check existing courses."
      );
    }

    const coursesData =
      await coursesResponse.json();

    const existingCourses = Array.isArray(
      coursesData
    )
      ? coursesData
      : coursesData?.results || [];

    // Case-insensitive duplicate check
    const duplicateCourse =
      existingCourses.find(
        (course) =>
          String(course?.course_name || "")
            .trim()
            .toLowerCase() ===
          courseName.toLowerCase()
      );

    // ==================================================
    // DUPLICATE COURSE FOUND
    // ==================================================

    if (duplicateCourse) {
      setSnackbarMessage(
        `Course "${courseName}" already exists in this branch.`
      );

      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      setSaving(false);
      return;
    }

    // ==================================================
    // SAVE COURSE
    // ==================================================

    const response = await fetch(
      `${API_BASE_URL}/api/course/courses/`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          course_name: courseName,
          course_duration:
            formData.course_duration.trim(),
          course_description:
            formData.course_description.trim(),
          basic_course_fee:
            parseFloat(
              formData.basic_course_fee
            ).toFixed(2),
          gst_percentage:
            parseFloat(
              formData.gst_percentage
            ).toFixed(2),
          organization:
            parseInt(formData.organization),
          branch:
            parseInt(selectedBranchId),
        }),
      }
    );

    // ==================================================
    // AUTH ERROR
    // ==================================================

    if (response.status === 401) {
      setSnackbarMessage(
        "Session expired. Please log in again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      localStorage.removeItem("token");
      sessionStorage.removeItem("token");

      setSaving(false);
      return;
    }

    // ==================================================
    // OTHER API ERROR
    // ==================================================

    if (!response.ok) {
      const errorText =
        await response.text();

      console.log(
        "Server error:",
        errorText
      );

      // Try to extract backend duplicate message
      try {
        const errorData =
          JSON.parse(errorText);

        const backendMessage =
          errorData?.course_name?.[0] ||
          errorData?.detail ||
          errorData?.message ||
          errorData?.error;

        if (backendMessage) {
          setSnackbarMessage(
            backendMessage
          );
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          setSaving(false);
          return;
        }
      } catch {
        // Ignore JSON parse error
      }

      throw new Error(
        `HTTP error! status: ${response.status}`
      );
    }

    // ==================================================
    // SUCCESS
    // ==================================================

    const newCourse =
      await response.json();

    setSnackbarMessage(
      `Course "${newCourse.course_name}" added successfully!`
    );

    setSnackbarSeverity("success");
    setOpenSnackbar(true);

    setShowSuccessModal(true);

    resetForm();
  } catch (error) {
    console.error(
      "Error adding course:",
      error
    );

    setSnackbarMessage(
      error.message ||
        "Failed to add course. Please try again."
    );

    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  } finally {
    setSaving(false);
  }
};

  const handleCancel = () => {
    resetForm();
  };

 const isFormValid = () => {
  return (
    formData.course_name.trim() &&
    formData.course_duration.trim() &&
    formData.course_description.trim() &&
    formData.basic_course_fee &&
    parseFloat(formData.basic_course_fee) > 0 &&
    formData.organization
    // Remove branch validation (handled globally)
    // && formData.branch
  );
};

  useEffect(() => {
    fetchOrganizations();
  }, []);

const fetchOrganizations = async () => {
  try {
    const orgResponse = await fetch(`${API_BASE_URL}/api/master/organizations/`, {
      headers: getAuthHeaders(),
    });

    if (!orgResponse.ok) {
      throw new Error("Failed to fetch organizations");
    }

    const orgData = await orgResponse.json();
    setOrganizations(orgData);

    const user = getStoredUser();
    const loggedInOrgId = user?.organization_id || user?.organization || '';

    // Try to find the logged-in user's own organization in the list
    const matchedOrg = orgData.find(
      (org) => String(org.id) === String(loggedInOrgId)
    );

    if (matchedOrg) {
      setFormData(prev => ({
        ...prev,
        organization: matchedOrg.id,
      }));
    } else if (orgData.length > 0) {
      // Fallback: if we can't match the user's org, use the first one
      setFormData(prev => ({
        ...prev,
        organization: orgData[0].id,
      }));
    }
  } catch (error) {
    console.error("Error fetching organizations:", error);
  }
};

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Course Details
          </Typography>
        </Box>

        <form onSubmit={handleSave}>


            {/* Organization Field - Auto select and disable when only one option */}
        {/* Organization Field - Always auto-filled from logged-in user and locked */}
<FormControl fullWidth sx={{ mb: 3 }} required disabled={saving}>
  <InputLabel
    sx={{
      color: theme.palette.text.primary,
      '&.Mui-focused': { color: theme.palette.primary.main }
    }}
  >
    Organization *
  </InputLabel>
  <Select
    name="organization"
    value={formData.organization}
    label="Organization *"
    onChange={handleInputChange}
    disabled={true}
    sx={{
      '&.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.action.disabled,
        },
      },
    }}
  >
    {organizations.length > 0 ? (
      organizations
        .filter((org) => String(org.id) === String(formData.organization))
        .map((org) => (
          <MenuItem key={org.id} value={org.id}>
            {org.name}
          </MenuItem>
        ))
    ) : (
      <MenuItem value={formData.organization}>
        Organization {formData.organization}
      </MenuItem>
    )}
  </Select>
  <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.info.main }}>
    Automatically set to your organization
  </Typography>
</FormControl>

         
          <TextField
            label="Course Name"
            name="course_name"
            variant="outlined"
            fullWidth
            value={formData.course_name}
            onChange={handleInputChange}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.course_name}
            helperText={formErrors.course_name}
            required
            disabled={saving}
          />

          <TextField
            label="Course Duration"
            name="course_duration"
            variant="outlined"
            fullWidth
            value={formData.course_duration}
            onChange={handleInputChange}
            placeholder="e.g., 8 Months"
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.course_duration}
            helperText={formErrors.course_duration}
            required
            disabled={saving}
          />

          <TextField
            label="Course Description"
            name="course_description"
            variant="outlined"
            fullWidth
            value={formData.course_description}
            onChange={handleInputChange}
            multiline
            rows={4}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.course_description}
            helperText={formErrors.course_description}
            required
            disabled={saving}
          />

          <TextField
            label="Basic Course Fee"
            name="basic_course_fee"
            variant="outlined"
            fullWidth
            value={formData.basic_course_fee}
            onChange={handleInputChange}
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">
                <span style={{ color: theme.palette.text.primary }}>₹</span>
              </InputAdornment>,
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            error={!!formErrors.basic_course_fee}
            helperText={formErrors.basic_course_fee}
            required
            disabled={saving}
          />

        

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip
              title={
                !isFormValid()
                  ? "Please fill all required fields correctly"
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!isFormValid() || saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {saving ? 'SAVING...' : 'SAVE'}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={saving}
            >
              RESET
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => { navigate(-1) }}
              disabled={saving}
            >
              BACK
            </Button>
          </Box>
        </form>

        {/* Success Modal */}
        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
                Course Added ✅
              </Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>
                Course details have been saved successfully.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddCourse;